import { ruleIdFromZeptoContent } from "site/services/zepto-editor";
import { splitRuleIntoSteps } from "site/utils/rule-native-markup";

export type LessonRulePayload = {
  ru: string;
  en?: string;
  fr?: string;
  vars?: Record<string, string>;
  titleRu?: string;
  titleEn?: string;
  titleFr?: string;
  refTitleAr?: string;
};

export type RuleLessonItem = {
  id: number;
  ruleId: string;
  titleRu?: string;
  titleEn?: string;
  titleFr?: string;
  refTitleAr?: string;
  vars?: Record<string, string>;
  ru: string;
  en?: string;
  fr?: string;
  steps: string[];
};

export type RuleZeptoRef = {
  id: number;
  ruleId: string;
};

export type LessonRuleEntry = { id: string } & LessonRulePayload;

export function normalizeLessonRule(raw: unknown): LessonRulePayload | null {
  if (raw === null || raw === undefined) {
    return null;
  }
  if (typeof raw === "string") {
    const ru = raw.trim();
    return ru ? { ru } : null;
  }
  if (typeof raw === "object") {
    const o = raw as Record<string, unknown>;
    const ru = typeof o.ru === "string" ? o.ru : "";
    if (!ru.trim()) {
      return null;
    }

    const vars: Record<string, string> = {};
    const rawVars = o.vars;
    if (rawVars && typeof rawVars === "object" && !Array.isArray(rawVars)) {
      for (const [key, value] of Object.entries(rawVars as Record<string, unknown>)) {
        if (typeof value === "string") {
          vars[key] = value;
        }
      }
    }

    const refTitleAr =
      typeof o.refTitleAr === "string" && o.refTitleAr ? o.refTitleAr : undefined;
    const titleRu = typeof o.titleRu === "string" && o.titleRu ? o.titleRu : undefined;
    const titleEn = typeof o.titleEn === "string" && o.titleEn ? o.titleEn : undefined;
    const titleFr = typeof o.titleFr === "string" && o.titleFr ? o.titleFr : undefined;
    const en = typeof o.en === "string" && o.en ? o.en : undefined;
    const fr = typeof o.fr === "string" && o.fr ? o.fr : undefined;

    const base: LessonRulePayload = { ru };
    if (Object.keys(vars).length) {
      base.vars = vars;
    }
    if (refTitleAr) {
      base.refTitleAr = refTitleAr;
    }
    if (titleRu) {
      base.titleRu = titleRu;
    }
    if (titleEn) {
      base.titleEn = titleEn;
    }
    if (titleFr) {
      base.titleFr = titleFr;
    }
    if (en) {
      base.en = en;
    }
    if (fr) {
      base.fr = fr;
    }
    return base;
  }
  return null;
}

/** Unique type-7 rules referenced in zepto, in lesson order. */
export function collectLessonRuleEntries(
  zepto: unknown[] | null | undefined,
  catalog: Record<string, unknown> | null | undefined,
): LessonRuleEntry[] {
  if (!zepto?.length || !catalog) {
    return [];
  }

  const seen = new Set<string>();
  const rules: LessonRuleEntry[] = [];

  for (const row of zepto) {
    if (!Array.isArray(row) || Number(row[0]) !== 7) {
      continue;
    }
    const ruleId = ruleIdFromZeptoContent(row[1]).trim();
    if (!ruleId || seen.has(ruleId)) {
      continue;
    }
    const rule = normalizeLessonRule(catalog[ruleId]);
    if (!rule?.ru) {
      continue;
    }
    seen.add(ruleId);
    rules.push({ id: ruleId, ...rule });
  }

  return rules;
}

export function buildRulesLessonItems(
  refs: RuleZeptoRef[],
  catalog: Record<string, unknown> | null | undefined,
): RuleLessonItem[] {
  const items: RuleLessonItem[] = [];
  for (const ref of refs) {
    const ruleId = ref.ruleId.trim();
    if (!ruleId) {
      continue;
    }
    const rule = normalizeLessonRule(catalog?.[ruleId]);
    if (!rule?.ru) {
      continue;
    }
    const steps = splitRuleIntoSteps(rule.ru);
    items.push({
      id: ref.id,
      ruleId,
      titleRu: rule.titleRu,
      titleEn: rule.titleEn,
      titleFr: rule.titleFr,
      refTitleAr: rule.refTitleAr,
      vars: rule.vars,
      ru: rule.ru,
      en: rule.en,
      fr: rule.fr,
      steps: steps.length ? steps : [rule.ru],
    });
  }
  return items;
}

export function ruleLessonItemStepCount(item: RuleLessonItem | null | undefined): number {
  return Math.max(1, item?.steps?.length ?? 0);
}
